import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { User } from '../../lib/types';
import { getApiUrl } from '../lib/api-config';

const USE_AUTH0 = import.meta.env.VITE_DISABLE_AUTH0 !== 'true';

// Decode JWT payload (without verification - verification happens on backend)
function decodeToken(token: string): any {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const decoded = JSON.parse(atob(parts[1]));
    return decoded;
  } catch (e) {
    console.error('Failed to decode token:', e);
    return null;
  }
}

export function useAuth() {
  // Only call useAuth0() if Auth0 is enabled
  const auth0 = USE_AUTH0 ? useAuth0() : null;
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  useEffect(() => {
    // If Auth0 is disabled, just finish loading
    if (!USE_AUTH0) {
      setLoading(false);
      return;
    }

    // Auth0 is enabled
    if (auth0?.isLoading) {
      return;
    }

    // Clear on logout
    if (!auth0?.isAuthenticated) {
      localStorage.removeItem('auth_email');
      localStorage.removeItem('id_token');
      localStorage.removeItem('access_token');
      setUser(null);
      setIdToken(null);
      setAccessToken(null);
      setLoading(false);
      return;
    }

    // Get tokens and sync user
    if (auth0?.isAuthenticated && auth0?.user?.email) {
      // Get ID token (contains user claims and custom role claim)
      auth0.getIdTokenClaims().then((claims) => {
        if (claims?.__raw) {
          setIdToken(claims.__raw);
          localStorage.setItem('id_token', claims.__raw);
          console.log('=== ID Token Claims ===');
          console.log('Full claims:', claims);
          console.log('Microsoft role claim:', claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']);
          console.log('user_role claim:', claims['user_role']);
          console.log('Email:', claims.email);
          console.log('Sub:', claims.sub);

          // Sync user AFTER getting token claims
          syncAuth0User(auth0.user, claims);
        }
      }).catch(err => console.error('Failed to get ID token:', err));

      // Get access token (for API calls)
      auth0.getAccessTokenSilently().then((token) => {
        setAccessToken(token);
        localStorage.setItem('access_token', token);
        console.log('Access token retrieved');
      }).catch(err => console.error('Failed to get access token:', err));
    }
  }, [auth0?.isAuthenticated, auth0?.user, auth0?.isLoading, auth0]);

  const syncAuth0User = async (auth0User: any, claims?: any) => {
    try {
      const email = auth0User.email;
      if (!email) {
        console.error('Auth0 user has no email');
        setUser(null);
        setLoading(false);
        return;
      }

      console.log('Syncing Auth0 user:', email);

      // Get role from ID token claims (Auth0 is authoritative)
      let roleFromToken = 'flow_TeamMember'; // default

      const claimsToUse = claims || (idToken ? decodeToken(idToken) : null);
      if (claimsToUse) {
        console.log('=== Extracting Role ===');
        console.log('Claims to use:', claimsToUse);

        // Check for role in multiple possible claim names
        const msRole = claimsToUse['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
        const userRole = claimsToUse['user_role'];
        const portfolioRole = claimsToUse['https://portfolio-tool/role'];
        const simpleRole = claimsToUse['role'];

        console.log('MS role claim:', msRole);
        console.log('user_role claim:', userRole);
        console.log('portfolio role claim:', portfolioRole);
        console.log('simple role claim:', simpleRole);

        const roleArray = msRole || userRole || portfolioRole || simpleRole;

        // Role might be an array, so get first element if it is
        if (Array.isArray(roleArray) && roleArray.length > 0) {
          roleFromToken = roleArray[0];
        } else if (typeof roleArray === 'string') {
          roleFromToken = roleArray;
        }

        console.log('Final role from Auth0 token:', roleFromToken);
      } else {
        console.log('No claims available, using default role');
      }

      // Fetch or create user in our database (with role from Auth0 token)
      const apiUrl = getApiUrl(`/users/me?email=${encodeURIComponent(email)}`);
      console.log('Fetching user from:', apiUrl);

      const response = await fetch(apiUrl, {
        headers: accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {},
      });
      const data = await response.json();

      console.log('User fetch response:', data);

      if (data.success && data.data) {
        console.log('User found, setting:', data.data);
        // Override database role with Auth0 token role (Auth0 is authoritative)
        const userWithAuthRole = { ...data.data, Role: roleFromToken };
        setUser(userWithAuthRole);
      } else {
        // User doesn't exist in our DB, create them with Auth0 role
        console.log('User not found, creating new user with role:', roleFromToken);
        const createResponse = await fetch(getApiUrl('/users'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({
            email,
            name: auth0User.name || auth0User.nickname || 'Auth0 User',
            role: roleFromToken,
          }),
        });

        const createData = await createResponse.json();
        console.log('Create user response:', createData);
        console.log('Create user response status:', createResponse.status);

        if (createData.success) {
          console.log('User created, setting:', createData.data);
          const userWithAuthRole = { ...createData.data, Role: roleFromToken };
          setUser(userWithAuthRole);
        } else {
          console.error('Failed to create user:', createData.error || 'Unknown error');
          setUser(null);
        }
      }
    } catch (error) {
      console.error('Failed to sync Auth0 user:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = () => {
    if (auth0) {
      auth0.loginWithRedirect();
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('auth_email');
    localStorage.removeItem('id_token');
    localStorage.removeItem('access_token');
    if (auth0) {
      auth0.logout({ logoutParams: { returnTo: window.location.origin } });
    }
  };

  // Helper to make authenticated API calls with token
  const fetchWithAuth = async (endpoint: string, options: RequestInit = {}) => {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (accessToken) {
      (headers as any)['Authorization'] = `Bearer ${accessToken}`;
    }

    return fetch(getApiUrl(endpoint), {
      ...options,
      headers,
    });
  };

  return {
    user,
    loading: loading || (auth0?.isLoading ?? false),
    login,
    logout,
    isAuthenticated: auth0?.isAuthenticated ?? false,
    accessToken,
    idToken,
    fetchWithAuth,
  };
}
